import { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { FaArrowRight, FaCheckCircle, FaDownload } from 'react-icons/fa';
import { FiZap } from 'react-icons/fi';
import api from '../../services/api';
import { rastrearTeste } from '../../services/rastreamentoTeste';
import { Aviso, Botao, Carregando, EtiquetaPreenchido, FaixaSucessoCompacta, FolhaContrato, Rodape } from './styles';

interface Props {
    idLocacao: string;
    aoConcluir: () => void;
}

interface CampoPreenchido {
    variavel: string;
    rotulo: string;
    valor: string;
}

type Estado = 'carregando' | 'docx' | 'pdf' | 'sem-contrato' | 'erro';

function agruparPorValor(campos: CampoPreenchido[]): CampoPreenchido[] {
    const porValor = new Map<string, CampoPreenchido>();
    for (const campo of campos) {
        const existente = porValor.get(campo.valor);
        if (existente) existente.rotulo = `${existente.rotulo} / ${campo.rotulo}`;
        else porValor.set(campo.valor, { ...campo });
    }
    return Array.from(porValor.values());
}

function destacarCamposPreenchidos(raiz: HTMLElement, campos: CampoPreenchido[]): Set<string> {
    const alvos = campos
        .filter(c => (c.valor ?? '').trim().length >= 3)
        .sort((a, b) => b.valor.length - a.valor.length);
    const achados = new Set<string>();
    if (!alvos.length) return achados;

    const nos: Text[] = [];
    const walker = document.createTreeWalker(raiz, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) nos.push(walker.currentNode as Text);

    for (const no of nos) {
        const texto = no.nodeValue ?? '';
        const trechos: { inicio: number; fim: number; campo: CampoPreenchido }[] = [];
        for (const campo of alvos) {
            let i = texto.indexOf(campo.valor);
            while (i >= 0) {
                const inicio = i;
                const fim = inicio + campo.valor.length;
                if (!trechos.some(t => inicio < t.fim && fim > t.inicio)) trechos.push({ inicio, fim, campo });
                i = texto.indexOf(campo.valor, fim);
            }
        }
        if (!trechos.length) continue;

        trechos.sort((a, b) => a.inicio - b.inicio);
        const fragmento = document.createDocumentFragment();
        let posicao = 0;
        for (const t of trechos) {
            if (t.inicio > posicao) fragmento.appendChild(document.createTextNode(texto.slice(posicao, t.inicio)));
            const marca = document.createElement('mark');
            marca.className = 'campo-auto';
            marca.dataset.variavel = t.campo.variavel;
            marca.title = `Preenchido automaticamente: ${t.campo.rotulo}`;
            marca.textContent = texto.slice(t.inicio, t.fim);
            fragmento.appendChild(marca);
            posicao = t.fim;
            achados.add(t.campo.variavel);
        }
        if (posicao < texto.length) fragmento.appendChild(document.createTextNode(texto.slice(posicao)));
        no.parentNode?.replaceChild(fragmento, no);
    }
    return achados;
}

// Logo depois da primeira locação: o locador vê o contrato dele já preenchido com os dados do
// cliente e do veículo. É a prova de que o modelo funciona. O Word é desenhado aqui mesmo (não
// depende do visualizador online da Microsoft, que exige link público).
export default function EtapaContratoPreenchido({ idLocacao, aoConcluir }: Props) {
    const folha = useRef<HTMLDivElement>(null);
    const [estado, setEstado] = useState<Estado>('carregando');
    const [link, setLink] = useState('');
    const [pdf, setPdf] = useState('');
    const [preenchidos, setPreenchidos] = useState<CampoPreenchido[]>([]);
    // Uma vez por montagem: o que a pessoa acabou vendo.
    const exibicaoRegistrada = useRef(false);
    const registrarExibicao = (exibiu: 'ok' | 'falhou' | 'sem_contrato') => {
        if (exibicaoRegistrada.current) return;
        exibicaoRegistrada.current = true;
        rastrearTeste('onboarding_contrato_preenchido_visto', { exibiu });
    };

    useEffect(() => {
        let ativo = true;
        let urlPdf = '';
        (async () => {
            try {
                const camposPromessa = api.get<CampoPreenchido[]>(`/Onboarding/CamposContrato/${idLocacao}`)
                    .then(r => r.data ?? [])
                    .catch(() => [] as CampoPreenchido[]);
                const { data } = await api.get(`/Locacao/ComDocumentoContrato/${idLocacao}`);
                const documento: string = data?.documentoContrato;
                if (!documento) { if (ativo) { registrarExibicao('sem_contrato'); setEstado('sem-contrato'); } return; }
                if (ativo) setLink(documento);

                const buffer = await (await fetch(documento)).arrayBuffer();
                if (!ativo) return;
                const cabecalho = String.fromCharCode(...Array.from(new Uint8Array(buffer).subarray(0, 4)));
                if (cabecalho === '%PDF') {
                    urlPdf = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
                    setPdf(urlPdf);
                    registrarExibicao('ok');
                    setEstado('pdf');
                    return;
                }
                setEstado('docx');
                const campos = agruparPorValor(await camposPromessa);
                setTimeout(() => {
                    if (!ativo || !folha.current) return;
                    const alvo = folha.current;
                    alvo.innerHTML = '';
                    renderAsync(buffer, alvo)
                        .then(() => {
                            if (!ativo) return;
                            registrarExibicao('ok');
                            const achados = destacarCamposPreenchidos(alvo, campos);
                            setPreenchidos(campos.filter(c => achados.has(c.variavel)));
                        })
                        .catch(() => { registrarExibicao('falhou'); setEstado('erro'); });
                }, 0);
            } catch {
                if (ativo) { registrarExibicao('falhou'); setEstado('erro'); }
            }
        })();
        return () => { ativo = false; if (urlPdf) URL.revokeObjectURL(urlPdf); };
    }, [idLocacao]);

    return (
        <>
            <FaixaSucessoCompacta>
                <FaCheckCircle />
                <strong>{estado === 'sem-contrato' || estado === 'erro' ? 'Locação criada.' : 'Locação criada e contrato pronto.'}</strong>
                {preenchidos.length > 0 && (
                    <EtiquetaPreenchido title="Os trechos marcados em amarelo vieram do cadastro, sem você digitar nada no contrato.">
                        <FiZap /> {preenchidos.length} {preenchidos.length === 1 ? 'informação preenchida' : 'informações preenchidas'} automaticamente
                    </EtiquetaPreenchido>
                )}
            </FaixaSucessoCompacta>

            {estado === 'carregando' && <FolhaContrato style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Carregando /></FolhaContrato>}
            {estado === 'docx' && <FolhaContrato ref={folha} />}
            {estado === 'pdf' && <FolhaContrato><iframe src={pdf} title="Contrato preenchido" /></FolhaContrato>}

            {estado === 'sem-contrato' && <Aviso style={{ marginTop: 12 }}>Esta locação foi criada sem contrato. Quando quiser, gere o contrato pela tela de Locação, no menu da locação. Agora é só continuar.</Aviso>}
            {estado === 'erro' && (
                <Aviso $tipo="erro" style={{ marginTop: 12 }}>
                    {link
                        ? 'Não conseguimos mostrar o contrato aqui. Você pode baixá-lo e conferir no seu computador.'
                        : 'Não conseguimos mostrar o contrato agora. Você pode vê-lo depois pela tela de Locação. Agora é só continuar.'}
                </Aviso>
            )}

            <Rodape style={{ marginTop: 12, paddingTop: 12 }}>
                {link ? <Botao as="a" href={link} target="_blank" rel="noopener noreferrer" $variante="link"><FaDownload /> Baixar o contrato</Botao> : <span />}
                <Botao type="button" onClick={aoConcluir}>Continuar <FaArrowRight /></Botao>
            </Rodape>
        </>
    );
}
